// src/EnhancedComponents/Layout.js
import React, { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Outlet, matchPath, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Avatar,
  Box,
  Drawer,
  IconButton,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import MenuOpenRoundedIcon from "@mui/icons-material/MenuOpenRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import Sidebar, { NAV_ITEMS } from "./Sidebar";
import ColorModeToggle from "./ColorModeToggle";
import ErrorBoundary from "./ErrorBoundary";
import { endSession, isAuthenticated } from "./auth";
import { DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED } from "./theme";

const COLLAPSE_KEY = "admin.sidebarCollapsed";
const SESSION_CHECK_MS = 30 * 1000;

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
};

export default function Layout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const drawerWidth = isDesktop && collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;
  const current = NAV_ITEMS.find((i) =>
    matchPath({ path: i.to, end: !!i.end }, location.pathname)
  );

  /* close the mobile drawer whenever the route changes */
  useEffect(() => setMobileOpen(false), [location.pathname]);

  /* persist the desktop collapse preference */
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch {
      /* storage unavailable (private mode) — ignore */
    }
  }, [collapsed]);

  const logout = useCallback(() => {
    endSession();
    navigate("/login", { replace: true });
  }, [navigate]);

  /* session watchdog: bounce to login once the 4 h cookie expires */
  const locationRef = useRef(location);
  locationRef.current = location;
  useEffect(() => {
    const check = () => {
      if (!isAuthenticated()) {
        navigate("/login", {
          replace: true,
          state: { from: locationRef.current, expired: true },
        });
      }
    };
    const id = setInterval(check, SESSION_CHECK_MS);
    window.addEventListener("focus", check);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", check);
    };
  }, [navigate]);

  const toggleNav = () => (isDesktop ? setCollapsed((c) => !c) : setMobileOpen((o) => !o));

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Skip link for keyboard / screen-reader users */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: "absolute",
          left: 8,
          top: -48,
          zIndex: (t) => t.zIndex.tooltip,
          px: 2,
          py: 1,
          borderRadius: 1,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          "&:focus": { top: 8 },
        }}
      >
        Skip to content
      </Box>

      {/* Sidebar: temporary drawer on mobile, permanent (collapsible) on desktop */}
      <Box
        component="aside"
        sx={{
          width: { md: drawerWidth },
          flexShrink: { md: 0 },
          transition: theme.transitions.create("width"),
        }}
      >
        {isDesktop ? (
          <Drawer
            variant="permanent"
            open
            slotProps={{
              paper: {
                sx: {
                  width: drawerWidth,
                  border: 0,
                  overflowX: "hidden",
                  transition: theme.transitions.create("width"),
                },
              },
            }}
          >
            <Sidebar collapsed={collapsed} onLogout={logout} />
          </Drawer>
        ) : (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            slotProps={{ paper: { sx: { width: DRAWER_WIDTH, border: 0 } } }}
          >
            <Sidebar onLogout={logout} />
          </Drawer>
        )}
      </Box>

      {/* Main column */}
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <AppBar
          position="sticky"
          color="inherit"
          elevation={0}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Toolbar sx={{ gap: 1, px: { xs: 1.5, sm: 2.5 } }}>
            <Tooltip title={isDesktop ? (collapsed ? "Expand menu" : "Collapse menu") : "Open menu"}>
              <IconButton
                edge="start"
                onClick={toggleNav}
                aria-label={isDesktop ? "Toggle sidebar" : "Open navigation menu"}
                aria-expanded={isDesktop ? !collapsed : mobileOpen}
              >
                {isDesktop && !collapsed ? <MenuOpenRoundedIcon /> : <MenuRoundedIcon />}
              </IconButton>
            </Tooltip>

            <Typography variant="h6" component="div" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {current?.label ?? "Admin"}
            </Typography>

            <ColorModeToggle />

            <Tooltip title="Account">
              <IconButton
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                aria-label="Account menu"
                aria-haspopup="menu"
                aria-controls={menuAnchor ? "account-menu" : undefined}
                aria-expanded={Boolean(menuAnchor)}
              >
                <Avatar sx={{ width: 34, height: 34, bgcolor: "primary.main", fontSize: 15 }}>
                  A
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              id="account-menu"
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle2">Administrator</Typography>
                <Typography variant="caption" color="text.secondary">
                  Bhopal Jamaat
                </Typography>
              </Box>
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  logout();
                }}
              >
                <ListItemIcon>
                  <LogoutRoundedIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box
          component="main"
          id="main-content"
          tabIndex={-1}
          sx={{
            flex: 1,
            p: { xs: 1.5, sm: 2.5, md: 3 },
            width: "100%",
            maxWidth: 1600,
            mx: "auto",
            outline: "none",
          }}
        >
          <ErrorBoundary key={location.pathname}>
            <Suspense fallback={<LinearProgress aria-label="Loading page" />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </Box>
      </Box>
    </Box>
  );
}
